/**
 * Native Bridge Protocol Types
 * Defines the message structure for bidirectional communication
 * between web and native iOS app
 */

// Base message types
export type MessageType = "request" | "response" | "event" | "subscribe" | "unsubscribe" | "subscription"

// Base message interface
interface BaseMessage {
  id: string
  type: MessageType
  timestamp: number
}

// Request message (Web → Native)
export interface NativeRequest extends BaseMessage {
  type: "request"
  method: string
  params?: unknown
  timeout?: number
}

// Response status
export type ResponseStatus = "success" | "error"

// Error structure
export interface NativeError {
  code: string
  message: string
  details?: unknown
}

/**
 * What the bridge rejects and throws with: a real Error (so stacks and
 * `instanceof Error` checks work) that still carries the native `code` and
 * `details` fields callers branch on.
 */
export class NativeBridgeError extends Error implements NativeError {
  readonly code: string
  readonly details?: unknown

  constructor(code: string, message: string, details?: unknown) {
    super(message)
    this.name = "NativeBridgeError"
    this.code = code
    this.details = details
  }
}

// Health and Workout specific error codes
export enum HealthErrorCode {
  HEALTH_AUTHORIZATION_DENIED = "HEALTH_AUTHORIZATION_DENIED",
  NOT_SUPPORTED = "NOT_SUPPORTED",
}

export enum WorkoutErrorCode {
  NO_APPLE_WATCH = "NO_APPLE_WATCH",
  WORKOUT_AUTHORIZATION_DENIED = "WORKOUT_AUTHORIZATION_DENIED",
  IOS_VERSION_TOO_OLD = "IOS_VERSION_TOO_OLD",
  WORKOUT_DATA_INVALID = "WORKOUT_DATA_INVALID",
  INVALID_REQUEST = "INVALID_REQUEST",
}

// Response message (Native → Web)
// iOS may return 'result' instead of 'data' for success responses
export interface NativeResponse extends BaseMessage {
  type: "response"
  status: ResponseStatus
  data?: unknown
  result?: unknown // iOS-specific field (alternative to data)
  error?: NativeError
}

// Event message (Native → Web)
export interface NativeEvent extends BaseMessage {
  type: "event"
  event: string
  data: unknown
}

// Subscription request (Web → Native)
export interface SubscriptionRequest extends BaseMessage {
  type: "subscribe"
  event: string
  params?: unknown
}

// Subscription status
export type SubscriptionStatus = "subscribed" | "unsubscribed" | "error"

// Subscription response (Native → Web)
export interface SubscriptionResponse extends BaseMessage {
  type: "subscription"
  status: SubscriptionStatus
  error?: NativeError
}

// Unsubscribe request (Web → Native)
export interface UnsubscribeRequest extends BaseMessage {
  type: "unsubscribe"
}

// Union type for all messages
export type NativeMessage =
  | NativeRequest
  | NativeResponse
  | NativeEvent
  | SubscriptionRequest
  | SubscriptionResponse
  | UnsubscribeRequest

// Error codes
export enum ErrorCode {
  TIMEOUT = "TIMEOUT",
  METHOD_NOT_FOUND = "METHOD_NOT_FOUND",
  INVALID_PARAMS = "INVALID_PARAMS",
  PERMISSION_DENIED = "PERMISSION_DENIED",
  DEVICE_NOT_FOUND = "DEVICE_NOT_FOUND",
  CONNECTION_FAILED = "CONNECTION_FAILED",
  ALREADY_CONNECTED = "ALREADY_CONNECTED",
  NOT_CONNECTED = "NOT_CONNECTED",
  CANCELLED = "CANCELLED",
  INTERNAL_ERROR = "INTERNAL_ERROR",
}

// Method definitions
// Lactate methods
export interface LactateConnectParams {
  timeout?: number
}

export interface LactateConnectResponse {
  deviceId: string
  deviceName: string
  connected: boolean
}

export interface LactateMeasurement {
  id: string
  timestamp: number
  value: number
  valueType: "Lactate"
}

export interface LactateGetMeasurementsResponse {
  measurements: LactateMeasurement[]
}

// --- Multi-device (lactateV2) ---------------------------------------------

export type LactometerFamily = "EN605" | "EN011"

export interface LactatePairedDevice {
  address: string
  name: string
  family: LactometerFamily
  /** The device the UI currently acts on. */
  isActive: boolean
  /** Currently reachable over a live BLE connection (only one device can be). */
  online: boolean
  /** Whether background auto-sync pulls from this device when it's discovered. */
  syncOnDiscovery: boolean
  addedAtMs: number
  lastSeenMs: number
}

export interface LactateListDevicesResponse {
  devices: LactatePairedDevice[]
  activeAddress: string
  /** Whether the phone's Bluetooth is on (absent on older native builds). */
  bluetoothEnabled?: boolean
}

/** One lactate zone boundary pair for an EN605 threshold upload (0x20). */
export interface LactateZoneRange {
  /** Range slot 1..5. */
  type: number
  /** Lower boundary, mmol/L. */
  low: number
  /** Upper boundary, mmol/L. */
  high: number
}

// Workout methods
export interface WorkoutCreateParams {
  workoutId: string
  location?: "indoor" | "outdoor"
}

export interface WorkoutCreateResponse {
  scheduled: boolean
  workoutId: string
}

export interface ScheduledWorkout {
  id: string
  name: string
  scheduledDate: number
  duration: number
}

export interface WorkoutGetScheduledResponse {
  workouts: ScheduledWorkout[]
}

// System methods
export interface SystemInfo {
  appVersion: string
  osVersion: string
  deviceModel: string
  buildNumber: string
}

export interface SystemPermissions {
  bluetooth: boolean
  notifications: boolean
  health: boolean
  camera: boolean
  photoLibrary: boolean
}

// Health methods
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface HealthConnectParams {}

export interface HealthConnectResponse {
  status: "success" | "failed"
  message?: string
  error?: NativeError
}

export interface HealthConnectionStatusResponse {
  connected: boolean
  available: boolean
  authorizationStatus: "notDetermined" | "denied" | "authorized"
  authorizedTypes?: string[]
}

export interface HealthAutoSyncStatusResponse {
  enabled: boolean
}

export interface HealthToggleAutoSyncParams {
  enabled: boolean
}

export interface HealthGetWorkoutsParams {
  days?: number
}

export interface HealthWorkout {
  id: string
  type: string
  date: string // ISO string
  duration: number // in seconds
  distance: number // in meters
  calories?: number
  sourceName?: string
  isImported: boolean
}

export interface HealthGetWorkoutsResponse {
  workouts: HealthWorkout[]
  total: number
}

export interface HealthExportWorkoutParams {
  workoutId: string
}

export interface HealthExportWorkoutResponse {
  data: string // Base64 encoded FIT file
  filename: string
}

export interface HealthSendWorkoutParams {
  workoutId: string
}

export interface HealthSendWorkoutResponse {
  status: "success" | "failed"
  message?: string
}

export interface HealthMarkWorkoutImportedParams {
  workoutId: string
}

// Re-export as namespace for backward compatibility
export const Methods = {
  Lactate: {
    ConnectParams: {} as LactateConnectParams,
    ConnectResponse: {} as LactateConnectResponse,
    Measurement: {} as LactateMeasurement,
    GetMeasurementsResponse: {} as LactateGetMeasurementsResponse,
  },
  Workout: {
    CreateParams: {} as WorkoutCreateParams,
    CreateResponse: {} as WorkoutCreateResponse,
    ScheduledWorkout: {} as ScheduledWorkout,
    GetScheduledResponse: {} as WorkoutGetScheduledResponse,
  },
  System: {
    SystemInfo: {} as SystemInfo,
    Permissions: {} as SystemPermissions,
  },
  Health: {
    ConnectParams: {} as HealthConnectParams,
    ConnectResponse: {} as HealthConnectResponse,
    ConnectionStatusResponse: {} as HealthConnectionStatusResponse,
    AutoSyncStatusResponse: {} as HealthAutoSyncStatusResponse,
    ToggleAutoSyncParams: {} as HealthToggleAutoSyncParams,
    GetWorkoutsParams: {} as HealthGetWorkoutsParams,
    Workout: {} as HealthWorkout,
    GetWorkoutsResponse: {} as HealthGetWorkoutsResponse,
    ExportWorkoutParams: {} as HealthExportWorkoutParams,
    ExportWorkoutResponse: {} as HealthExportWorkoutResponse,
    SendWorkoutParams: {} as HealthSendWorkoutParams,
    SendWorkoutResponse: {} as HealthSendWorkoutResponse,
    MarkWorkoutImportedParams: {} as HealthMarkWorkoutImportedParams,
  },
}

// Event definitions
// Lactate events
export interface LactateConnectedEvent {
  deviceId: string
  deviceName: string
}

export interface LactateDisconnectedEvent {
  deviceId: string
  reason?: string
}

export interface LactateMeasurementEvent {
  measurement: LactateMeasurement
}

export interface LactateErrorEvent {
  error: NativeError
  deviceId?: string
}

export interface LactateAutoSyncStatusResponse {
  enabled: boolean
}

export interface LactateConnectionStatusResponse {
  isConnected: boolean
  isPaired: boolean
  isScanning: boolean
  device?: {
    deviceId: string
    deviceName: string
  }
  // Multi-device fields (lactateV2). Absent on older native builds.
  devices?: LactatePairedDevice[]
  activeAddress?: string
}

export interface LactateActiveDeviceChangedEvent {
  address: string
}

export interface LactateDeviceForgottenEvent {
  address: string
}

export interface LactateDevicePairedEvent {
  address: string
  name: string
}

// Workout events
export interface WorkoutScheduledEvent {
  workoutId: string
  scheduledDate: number
}

export interface WorkoutStartedEvent {
  workoutId: string
  startTime: number
}

export interface WorkoutCompletedEvent {
  workoutId: string
  endTime: number
  duration: number
}

export interface WorkoutCancelledEvent {
  workoutId: string
  reason?: string
}

// System events
export interface SystemForegroundEvent {
  timestamp: number
}

export interface SystemBackgroundEvent {
  timestamp: number
}

// Re-export as namespace for backward compatibility
export const Events = {
  Lactate: {
    ConnectedEvent: {} as LactateConnectedEvent,
    DisconnectedEvent: {} as LactateDisconnectedEvent,
    MeasurementEvent: {} as LactateMeasurementEvent,
    ErrorEvent: {} as LactateErrorEvent,
  },
  Workout: {
    ScheduledEvent: {} as WorkoutScheduledEvent,
    StartedEvent: {} as WorkoutStartedEvent,
    CompletedEvent: {} as WorkoutCompletedEvent,
    CancelledEvent: {} as WorkoutCancelledEvent,
  },
  System: {
    ForegroundEvent: {} as SystemForegroundEvent,
    BackgroundEvent: {} as SystemBackgroundEvent,
  },
}

// Type guards
export function isNativeRequest(msg: NativeMessage): msg is NativeRequest {
  return msg.type === "request"
}

export function isNativeResponse(msg: NativeMessage): msg is NativeResponse {
  return msg.type === "response"
}

export function isNativeEvent(msg: NativeMessage): msg is NativeEvent {
  return msg.type === "event"
}

export function isSubscriptionRequest(msg: NativeMessage): msg is SubscriptionRequest {
  return msg.type === "subscribe"
}

export function isSubscriptionResponse(msg: NativeMessage): msg is SubscriptionResponse {
  return msg.type === "subscription"
}

export function isUnsubscribeRequest(msg: NativeMessage): msg is UnsubscribeRequest {
  return msg.type === "unsubscribe"
}
